import { useState, useCallback, useRef, useEffect } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, uploadInteractionMetric } from '../../common/constants/metrics';
import type { Source } from '../../common/constants/source';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useGetCSVImportClient } from '../../common/utils/csv-import-clients';
import type { UploadUrlResponse } from '../../common/utils/csv-import-clients/types';
import type { UseUploadCsv } from './types';
import { fetchUploadUrlAndMigrationId, uploadFileToS3 } from './utils';

export const useUploadCsv = (source: Source): UseUploadCsv => {
	const cloudId = useCloudId();
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.UPLOAD_CSV,
	);

	const analyticsAttributes = useAnalyticsAttributesContext();
	const [isFileUploading, setIsFileUploading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);
	const client = useGetCSVImportClient(source);

	useEffect(
		() => () => {
			abortControllerRef?.current?.abort();
		},
		[abortControllerRef],
	);

	const uploadCsv = useCallback(
		async (file: File, projectKey: string) => {
			setIsFileUploading(true);
			setHasErrorOccurred(false);
			uploadInteractionMetric.start();
			onExperienceStart();
			abortControllerRef?.current?.abort();
			abortControllerRef.current = new AbortController();

			try {
				let response: UploadUrlResponse;
				if (ff('enable-importing-data-to-existing-project_k8uub')) {
					response = await client.getUploadUrlAndMigrationId({
						signal: abortControllerRef?.current?.signal,
						projectKey,
					});
				} else {
					response = await fetchUploadUrlAndMigrationId(
						cloudId,
						abortControllerRef?.current?.signal,
					);
				}
				const { uploadUrl, migrationId } = response;

				await uploadFileToS3(uploadUrl, file, abortControllerRef?.current?.signal);

				uploadInteractionMetric.stop();
				onExperienceSuccess(analyticsAttributes);

				setIsFileUploading(false);
				return migrationId;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				uploadInteractionMetric.stop();
				if (!abortControllerRef?.current?.signal?.aborted) {
					onExperienceFail(APP_IDENTIFIER, error, analyticsAttributes);
					fireErrorAnalytics({
						meta: {
							id: 'upload-error',
							packageName: APP_IDENTIFIER,
							teamName: APP_TEAM_NAME,
						},
						sendToPrivacyUnsafeSplunk: true,
						error,
					});
					setHasErrorOccurred(true);
				}
				setIsFileUploading(false);
				return null;
			}
		},
		[
			onExperienceStart,
			onExperienceSuccess,
			analyticsAttributes,
			client,
			cloudId,
			onExperienceFail,
		],
	);

	return { isFileUploading, hasErrorOccurred, uploadCsv };
};
